import type { DynamicInvoiceMeasurementChartMeter } from '@weft/api-definition';
import type { ProductLineItem } from '@weft/services';
import { getInstanceUsageMapKey, isTiersPricing } from '@weft/services';
import { isCommitmentLineItem, isTiersProductVariant } from '@weft/services';
import { isUsageLineItem } from '@weft/services';
import { DEFAULT_CURRENCY, INVOICE_REVIEW_STATUSES, type Currency } from '@weft/types';
import type { UsageLineItem, LineItemProduct, LineItemProductVariant } from '@weft/types';
import type { PriceTier, CommitmentLineItem, CommitmentTierUsageData, TierUsageData } from '@weft/types';
import type { Nullable, Optional } from '@weft/utils';
import { getObjectPropertyOrDefault, raise } from '@weft/utils';
import { isEmptyObject } from '@weft/utils';
import { getRequiredObjectProperty, isNotNullOrUndefined } from '@weft/utils';
import { getCurrencySymbol } from 'apps/app/src/services/currency-symbol-mapper';
import React, { useState } from 'react';
import { Divider } from '../../../../../components/design/divider/Divider';
import Table from '../../../../../components/design/table/Table';
import { buildCommitmentLineItemTiersTableColumns } from '../../../InvoiceViewPage/commitment-line-item-tiers-table-columns-builder';
import type { CommitmentLineItemTierRow } from '../../../InvoiceViewPage/invoice-types/commitment-line-item-tier-row';
import { useDynamicInvoice } from '../../dynamic-invoice-context';
import { buildInvoiceTiersTableColumns } from '../../dynamic-invoice-tiers-table-columns-builder';
import type { DynamicInvoiceTiersTableRow } from '../../dynamic-invoice-view-page-types/dynamic-invoice-tiers-table-row';
import { UsageLineItemBarChart } from '../UsageLineItemBarChart';
import { ResetDialog } from './ResetDialog';

type TiersUsageLineItemBodyProps = {
  lineItemProduct: LineItemProduct,
  productLineItem: ProductLineItem,
  productMetersMeasurementChartData: Optional<DynamicInvoiceMeasurementChartMeter>,
  selectedInstanceKeyValue: string | undefined;
};

export type ResetLineItemData = {
  lineItemId: string;
  productVariantId: string;
  tierIndex: number;
};

export const TiersUsageLineItemBody:React.FC<TiersUsageLineItemBodyProps> = ({
  lineItemProduct,
  productLineItem,
  productMetersMeasurementChartData,
  selectedInstanceKeyValue,
}) => {
  const [resetLineItemData, setResetLineItemData] = useState<Nullable<ResetLineItemData>>(null);

  const onResetClick = (lineItemId: string, productVariantId: string, tierIndex: number) => {
    setResetLineItemData({
      lineItemId,
      productVariantId,
      tierIndex,
    });
  };

  const { dynamicInvoiceData } = useDynamicInvoice();
  const isEditDisabled = dynamicInvoiceData.invoice.reviewStatus === INVOICE_REVIEW_STATUSES.Approved;
  const currency = dynamicInvoiceData.customer.settings?.currency ?? DEFAULT_CURRENCY;
  const currencySymbol = getCurrencySymbol(currency);

  return (
    <>
      {lineItemProduct.variants.map((productVariant, index) => {
        const tiers = getTiers(productVariant);
        const isFlatRateVariant = isTiersPricing(productVariant.pricing) && productVariant.pricing.priceConfiguration.isFlatRate;
        const commitmentColumns = buildCommitmentLineItemTiersTableColumns(currencySymbol);
        
        return (
          <React.Fragment key={index}>
            {index !== 0 && <Divider className="my-4"/>}
            <div className="mb-4">
              {
                isCommitmentLineItem(productLineItem) && (
                  <Table {...{
                    rows: buildCommitmentRows(productLineItem, productVariant),
                    columns: commitmentColumns,
                    tableClassName: 'w-[850px]',
                    headerClassName: 'border-b border-b-line w-4',
                    cellClassName: 'py-1.5',
                  }}
                  />
                )
              }
              {
                (isUsageLineItem(productLineItem) && (isEmptyObject(productLineItem.instanceUsageMap) || isNotNullOrUndefined(selectedInstanceKeyValue))) && (
                  <Table {...{
                    rows: buildRows(tiers, currency, getTierUsageData(productLineItem, productVariant.productVariantId!, selectedInstanceKeyValue) ?? []),
                    columns: buildInvoiceTiersTableColumns(
                      productLineItem.id,
                      productVariant.productVariantId!,
                      currency,
                      isEditDisabled,
                      isFlatRateVariant,
                      onResetClick,
                    ),
                    tableClassName: 'w-[850px]',
                    headerClassName: 'border-b border-b-line w-4',
                    cellClassName: 'py-1.5',
                  }}
                  />
                )
              }
              <ResetDialog
                resetLineItemData={resetLineItemData}
                setResetLineItemData={setResetLineItemData}
              />
            </div>
            {isNotNullOrUndefined(productVariant.meter) && isNotNullOrUndefined(productMetersMeasurementChartData) && (productVariant.meter.meterId in productMetersMeasurementChartData) && (
              <UsageLineItemBarChart
                datasetLabel={productVariant.meter?.name ?? ''}
                meterUsageData={getRequiredObjectProperty(productMetersMeasurementChartData, productVariant.meter.meterId)}
                selectedInstanceKeyValue={selectedInstanceKeyValue}
                period={productVariant.periodLimit ?? dynamicInvoiceData.invoice.billingCycle}
              />
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

function getTierUsageData(usageLineItem: UsageLineItem, productVariantId: string, selectedInstanceKeyValue: string | undefined) {
  return isNotNullOrUndefined(selectedInstanceKeyValue) && isNotNullOrUndefined(usageLineItem.instanceUsageMap) 
    ? usageLineItem.instanceUsageMap[getInstanceUsageMapKey(productVariantId, selectedInstanceKeyValue)] 
    : usageLineItem.tiersUsageMap[productVariantId];
}

function buildRows(tiers: PriceTier[], currency: Currency, tiersUsageData: TierUsageData[] | undefined = []) {
  return tiers.map<DynamicInvoiceTiersTableRow>((tier, index) => {
    const tierUsageData = Object.values(tiersUsageData).find(({ tierIndex }) => tierIndex === index);
    const discount = tierUsageData?.discountConfiguration ?? undefined; //TODO: Fix the null issue

    return ({
      ...tier,
      tierUnits: tierUsageData?.periodUnits ?? 0,
      discount,
      tierIndex: index,
      currency,
    });
  });
}

function buildCommitmentRows(lineItem: CommitmentLineItem, lineItemProductVariant: LineItemProductVariant) {
  if (!isTiersProductVariant(lineItemProductVariant)) {
    return raise('Expected product variant to be a tiers product variant');
  }

  const { tiers } = lineItemProductVariant.pricing.priceConfiguration;
  const tiersUsageData = getObjectPropertyOrDefault(lineItem.commitmentTiersUsageMap, lineItemProductVariant.productVariantId!, []);

  return tiersUsageData.map(tierUsageData => buildCommitmentLineItemTierRow(tiers, tierUsageData)).sort((previous, current) => previous.tierIndex - current.tierIndex);
}

function buildCommitmentLineItemTierRow(tiers: PriceTier[], tierUsageData: CommitmentTierUsageData): CommitmentLineItemTierRow {
  const tier = tiers[tierUsageData.tierIndex];

  return {
    ...tier,
    rate: tier.price,
    ...tierUsageData,
  };
}

function getTiers(lineItemProductVariant: LineItemProductVariant) {
  if (!isTiersProductVariant(lineItemProductVariant)) {
    return raise('Expected product variant to be a tiers product variant');
  }
  
  return lineItemProductVariant.pricing.priceConfiguration.tiers;
}
